import { makeAutoObservable, runInAction } from 'mobx'
import apiClient from '../utils/apiClient'
import logger from '../utils/logger'

class HardwareSetStore {
  // Наборы фурнитуры для выбранного продукта
  hardwareSets = []
  currentProduct = null
  partnership = null
  
  // Фурнитура из выбранного набора (только для справки, без цен)
  hardwareItems = []
  currentHardwareSet = null
  
  // Состояния загрузки
  isLoading = false
  isLoadingItems = false
  error = null

  constructor() {
    makeAutoObservable(this)
  }

  // Получение наборов фурнитуры для продукта партнера
  async fetchProductHardwareSets(productId) {
    if (!productId) {
      logger.error('ProductId is required for fetchProductHardwareSets')
      return
    }

    runInAction(() => {
      this.isLoading = true
      this.error = null
    })

    try {
      const response = await apiClient.get(`/partnerships/partner-hardware-sets/${productId}`)
      
      runInAction(() => {
        this.hardwareSets = response.data.hardwareSets
        this.currentProduct = response.data.product
        this.partnership = response.data.partnership
        this.isLoading = false
        
        // Сбрасываем фурнитуру при смене продукта
        this.hardwareItems = []
        this.currentHardwareSet = null
      })

      logger.log('Наборы фурнитуры продукта загружены:', response.data.count)
      return response.data

    } catch (error) {
      runInAction(() => {
        this.error = error.response?.data?.error || 'Ошибка при загрузке наборов фурнитуры'
        this.isLoading = false
        this.hardwareSets = []
        this.currentProduct = null
        this.partnership = null
      })

      logger.error('Ошибка при загрузке наборов фурнитуры продукта:', error)
      throw error
    }
  }

  // Получение фурнитуры из выбранного набора (для справки)
  async fetchHardwareItems(hardwareSetId) {
    if (!hardwareSetId) {
      logger.error('HardwareSetId is required for fetchHardwareItems')
      return
    }

    runInAction(() => {
      this.isLoadingItems = true
      this.error = null
    })

    try {
      const response = await apiClient.get(`/partnerships/partner-hardware-items/${hardwareSetId}`)
      
      runInAction(() => {
        // Сохраняем оригинальные данные для отслеживания замен
        this.hardwareItems = response.data.hardwareItems.map(item => ({
          ...item,
          original_hardware_item_id: item.hardware_item_id,
          original_hardware: { ...item.hardware } // глубокая копия оригинальной фурнитуры
        }))
        this.currentHardwareSet = response.data.hardwareSet
        this.isLoadingItems = false
      })

      logger.log('Фурнитура набора загружена:', response.data.count, 'позиций (только для справки)')
      return response.data

    } catch (error) {
      runInAction(() => {
        this.error = error.response?.data?.error || 'Ошибка при загрузке фурнитуры набора'
        this.isLoadingItems = false
        this.hardwareItems = []
        this.currentHardwareSet = null
      })

      logger.error('Ошибка при загрузке фурнитуры набора:', error)
      throw error
    }
  }

  // Получить набор фурнитуры по ID
  getHardwareSetById(hardwareSetId) {
    return this.hardwareSets.find(hardwareSet => hardwareSet.id === hardwareSetId)
  }

  // Получить позицию фурнитуры по ID
  getHardwareItemById(hardwareItemId) {
    return this.hardwareItems.find(item => item.hardware_item_id === hardwareItemId)
  }

  // Очистка ошибок
  clearError() {
    runInAction(() => {
      this.error = null
    })
  }

  // Сброс состояния
  reset() {
    runInAction(() => {
      this.hardwareSets = []
      this.currentProduct = null
      this.partnership = null
      this.hardwareItems = []
      this.currentHardwareSet = null
      this.isLoading = false
      this.isLoadingItems = false
      this.error = null
    })
  }

  // Сброс только фурнитуры (при смене набора)
  resetHardwareItems() {
    runInAction(() => {
      this.hardwareItems = []
      this.currentHardwareSet = null
      this.isLoadingItems = false
    })
  }

  // Выполнить замену фурнитуры (только локально, без сохранения в БД)
  replaceHardwareLocally(mappingId, alternativeHardwareItemId) {
    if (!mappingId || !alternativeHardwareItemId) {
      logger.error('MappingId and alternativeHardwareItemId are required for replaceHardware')
      return
    }

    logger.log('🔄 Начинаем замену:', { mappingId, alternativeHardwareItemId })

    runInAction(() => {
      // Находим позицию для замены
      const mappingIndex = this.hardwareItems.findIndex(item => item.id === mappingId)
      
      if (mappingIndex === -1) {
        logger.error('Mapping item not found:', mappingId)
        return
      }

      const currentMapping = this.hardwareItems[mappingIndex]
      logger.log('Текущая позиция до замены:', {
        id: currentMapping.id,
        current_hardware_id: currentMapping.hardware_item_id,
        original_hardware_id: currentMapping.original_hardware_item_id,
        hardware_name: currentMapping.hardware?.name || currentMapping.hardware?.display_name
      })
      
      // Проверяем - это возврат к оригиналу?
      const isOriginalReplacement = alternativeHardwareItemId === currentMapping.original_hardware_item_id
      logger.log('Это возврат к оригиналу?', isOriginalReplacement)
      
      let newHardware
      let replacement = null
      
      if (isOriginalReplacement) {
        // Возвращаемся к оригинальной фурнитуре
        newHardware = { ...currentMapping.original_hardware }
        logger.log('Возвращаемся к оригинальной фурнитуре:', newHardware)
      } else {
        // Находим альтернативную фурнитуру в возможных заменах
        replacement = currentMapping.possible_replacements?.find(
          rep => rep.alternative_hardware_item_id === alternativeHardwareItemId
        )
        
        if (!replacement) {
          logger.error('Replacement not found in possible_replacements:', {
            alternativeHardwareItemId,
            available_replacements: currentMapping.possible_replacements?.map(r => r.alternative_hardware_item_id)
          })
          return
        }

        // Создаем новый объект фурнитуры для замены
        newHardware = {
          id: replacement.alternative_hardware_item_id,
          name: replacement.alternative_hardware.name,
          display_name: replacement.alternative_hardware.display_name,
          article: replacement.alternative_hardware.article,
          img_url: replacement.alternative_hardware.img_url
        }
      }

      // Обновляем позицию в массиве
      this.hardwareItems[mappingIndex] = {
        ...currentMapping,
        hardware_item_id: alternativeHardwareItemId,
        hardware: newHardware,
        // Сохраняем оригинальные данные если их еще нет
        original_hardware_item_id: currentMapping.original_hardware_item_id || currentMapping.hardware_item_id,
        original_hardware: currentMapping.original_hardware || { ...currentMapping.hardware }
      }

      logger.log('✅ Позиция после замены:', {
        id: this.hardwareItems[mappingIndex].id,
        new_hardware_id: this.hardwareItems[mappingIndex].hardware_item_id,
        original_hardware_id: this.hardwareItems[mappingIndex].original_hardware_item_id,
        new_hardware_name: this.hardwareItems[mappingIndex].hardware?.name || this.hardwareItems[mappingIndex].hardware?.display_name
      })
    })

    logger.log('🎉 Локальная замена фурнитуры выполнена:', { mappingId, alternativeHardwareItemId })
  }

  // Геттеры
  get hasHardwareSets() {
    return this.hardwareSets.length > 0
  }

  get hasHardwareItems() {
    return this.hardwareItems.length > 0
  }

  get partnerName() {
    return this.partnership?.manufacturer_name || null
  }

  get productName() {
    return this.currentProduct?.name || null
  }

  get hardwareSetName() {
    return this.currentHardwareSet?.name || null
  }

  // Сортированные наборы по sort_order и названию
  get sortedHardwareSets() {
    return [...this.hardwareSets].sort((a, b) => {
      if (a.sort_order !== b.sort_order) {
        return a.sort_order - b.sort_order
      }
      return a.name.localeCompare(b.name)
    })
  }

  // Сортированная фурнитура по sort_order
  get sortedHardwareItems() {
    return [...this.hardwareItems].sort((a, b) => {
      if (a.sort_order !== b.sort_order) {
        return a.sort_order - b.sort_order
      }
      return a.hardware.name.localeCompare(b.hardware.name)
    })
  }

  // Получить расширенный список замен для позиции (включая возврат к оригиналу)
  getExtendedReplacementsForItem(item) {
    const replacements = [...(item.possible_replacements || [])]
    
    logger.log('getExtendedReplacementsForItem для позиции:', {
      id: item.id,
      current_hardware_id: item.hardware_item_id,
      original_hardware_id: item.original_hardware_item_id,
      is_replaced: item.hardware_item_id !== item.original_hardware_item_id,
      original_hardware_exists: !!item.original_hardware
    })
    
    // Если позиция была заменена, добавляем оригинальную фурнитуру как опцию возврата
    if (item.original_hardware_item_id && item.hardware_item_id !== item.original_hardware_item_id) {
      // Находим оригинальную фурнитуру из initial data (нужно сохранить при загрузке)
      const originalHardware = item.original_hardware
      if (originalHardware) {
        const originalReplacement = {
          id: 'original-' + item.id,
          source_mapping_id: item.id,
          alternative_hardware_item_id: item.original_hardware_item_id,
          alternative_hardware: originalHardware,
          is_original: true // флаг что это возврат к оригиналу
        }
        replacements.unshift(originalReplacement) // добавляем в начало списка
        logger.log('Добавлена оригинальная замена:', originalReplacement)
      } else {
        logger.error('original_hardware отсутствует для позиции:', item.id)
      }
    }
    
    logger.log('Итоговый список замен:', replacements.length, replacements)
    return replacements
  }

  // Количество позиций фурнитуры
  get totalQuantity() {
    return this.hardwareItems.reduce((sum, item) => sum + item.quantity, 0)
  }

  // Геттер для отслеживания локальных замен (для отладки)
  get localReplacements() {
    return this.hardwareItems
      .filter(item => item.original_hardware_item_id && item.hardware_item_id !== item.original_hardware_item_id)
      .map(item => ({
        mappingId: item.id,
        originalId: item.original_hardware_item_id,
        replacedWithId: item.hardware_item_id,
        replacedWithName: item.hardware?.display_name || item.hardware?.name
      }))
  }
}

const hardwareSetStore = new HardwareSetStore()
export default hardwareSetStore 